import { Component, ViewChild, ElementRef, OnDestroy, ChangeDetectorRef, ChangeDetectionStrategy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { FormatService } from '../../format.service';
import { LoanCalculatorService } from '../../loan-calculator.service';
import { ChartService } from '../../chart.service';
import { ValidationService } from '../../validation.service';
import {
  LoanSummary, MonthlyPayment, Prepayment, ScheduleResult, YearlyPayment,
  PrepaymentFieldError, PERSONAL_LOAN_CONSTRAINTS
} from '../../models';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'app-personal-loan-calculator',
  standalone: true,
  imports: [CommonModule, FormsModule],
  providers: [ChartService],
  templateUrl: './personal-loan-calculator.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./personal-loan-calculator.scss']
})
export class PersonalLoanCalculator implements OnDestroy {
  fmt = inject(FormatService);
  private calc = inject(LoanCalculatorService);
  private charts = inject(ChartService);
  private vld = inject(ValidationService);
  private cdr = inject(ChangeDetectorRef);


  @ViewChild('pieChartRef')  pieChartRef!:  ElementRef<HTMLCanvasElement>;
  @ViewChild('barChartRef')  barChartRef!:  ElementRef<HTMLCanvasElement>;
  @ViewChild('lineChartRef') lineChartRef!: ElementRef<HTMLCanvasElement>;

  readonly C = PERSONAL_LOAN_CONSTRAINTS;
  readonly Math = Math;

  // ── Loan inputs ──────────────────────────────────────────────────────────
  loanAmount = 500_000;
  annualInterestRate = 13;
  loanTermYears = 3;
  loanStartDate:       string = this.currentMonthStr();

  // ── Prepayment state ─────────────────────────────────────────────────────
  prepayments:        Prepayment[] = [];
  prepaymentOption:   'reduce_tenure' | 'reduce_emi' = 'reduce_tenure';
  newPrepaymentMonth = 6;
  newPrepaymentAmount = 25_000;
  private nextId = 1;

  // ── Validation ───────────────────────────────────────────────────────────
  formErrors:       Record<string, string> = {};
  prepayErrors:     PrepaymentFieldError   = {};
  touched:          Record<string, boolean>= {};
  prepayTouched:    Record<string, boolean>= {};
  showFormErrors    = false;

  // ── Results ──────────────────────────────────────────────────────────────
  calculated                = false;
  calculating                = false;
  calcError                = false;
  baseSummary:      LoanSummary | null     = null;
  finalSummary:     LoanSummary | null     = null;
  monthlySchedule:  MonthlyPayment[]       = [];
  yearlySchedule:   YearlyPayment[]        = [];
  hasPrepayments                = false;
  activeTab:        'yearly' | 'monthly'   = 'yearly';
  expandedYear:     number | null          = null;

  private currentMonthStr(): string {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  }

  get maxLoanMonths(): number { return Math.floor(this.loanTermYears || 0) * 12; }

  get interestSaved(): number {
    if (!this.baseSummary || !this.finalSummary) return 0;
    return Math.max(0, this.baseSummary.totalInterest - this.finalSummary.totalInterest);
  }

  get monthsSaved(): number {
    if (!this.baseSummary || !this.finalSummary) return 0;
    return Math.max(0, this.baseSummary.totalMonths - this.finalSummary.totalMonths);
  }

  get isFormValid(): boolean {
    return this.validateForm().valid;
  }

  private validateForm() {
    const result = this.vld.validateLoanForm({
      loanAmount:         this.loanAmount,
      annualInterestRate: this.annualInterestRate,
      loanTermYears:      this.loanTermYears,
      loanStartDate:      this.loanStartDate,
      borrowerAge:        null,
      monthlyIncome:      null
    }, this.C);
    this.formErrors = result.errors;
    return result;
  }

  onFieldChange(field: string): void {
    this.touched[field] = true;
    this.validateForm();
    if (this.calculated) {
      this.calculated = false;
      this.charts.destroyAll();
    }
  }

  onFieldBlur(field: string): void {
    this.touched[field] = true;
    this.clampField(field);
    this.validateForm();
  }

  private clampField(field: string): void {
    switch (field) {
      case 'loanAmount':
        if (this.loanAmount < 0) this.loanAmount = 0;
        break;
      case 'annualInterestRate':
        if (this.annualInterestRate < 0) this.annualInterestRate = 0;
        if (this.annualInterestRate > this.C.rate.max) this.annualInterestRate = this.C.rate.max;
        break;
      case 'loanTermYears':
        if (this.loanTermYears < 0) this.loanTermYears = 0;
        if (this.loanTermYears > this.C.term.max) this.loanTermYears = this.C.term.max;
        this.loanTermYears = Math.floor(this.loanTermYears);
        if (this.prepayments.length) {
          const max = this.maxLoanMonths;
          this.prepayments = this.prepayments.filter(p => p.month <= max);
        }
        break;
    }
  }

  fieldError(field: string): string | null {
    return (this.touched[field] || this.showFormErrors) && this.formErrors[field]
      ? this.formErrors[field]
      : null;
  }

  hasError(field: string): boolean { return !!this.fieldError(field); }

  onPrepayFieldChange(field: 'month' | 'amount'): void {
    this.prepayTouched[field] = true;
    this.validatePrepayFields();
  }

  onPrepayFieldBlur(field: 'month' | 'amount'): void {
    this.prepayTouched[field] = true;
    if (field === 'month' && this.newPrepaymentMonth < 0) this.newPrepaymentMonth = 1;
    if (field === 'amount' && this.newPrepaymentAmount < 0) this.newPrepaymentAmount = 0;
    this.validatePrepayFields();
  }

  private validatePrepayFields(): void {
    this.prepayErrors = this.vld.validatePrepayment(
      this.newPrepaymentMonth, this.newPrepaymentAmount, this.maxLoanMonths || 84, this.C
    );
  }

  get prepayFieldsValid(): boolean {
    const e = this.vld.validatePrepayment(
      this.newPrepaymentMonth, this.newPrepaymentAmount, this.maxLoanMonths || 84, this.C
    );
    return Object.keys(e).length === 0;
  }

  prepayError(field: 'month' | 'amount'): string | null {
    return this.prepayTouched[field] && this.prepayErrors[field]
      ? this.prepayErrors[field]!
      : null;
  }

  addPrepayment(): void {
    this.prepayTouched = { month: true, amount: true };
    this.validatePrepayFields();
    if (!this.prepayFieldsValid) return;

    const month  = Math.floor(this.newPrepaymentMonth);
    const amount = Math.round(this.newPrepaymentAmount);

    const existing = this.prepayments.find(p => p.month === month);
    if (existing) {
      existing.amount += amount;
      this.prepayments = [...this.prepayments];
    } else {
      this.prepayments = [...this.prepayments, { id: this.nextId++, month, amount }]
        .sort((a, b) => a.month - b.month);
    }

    this.newPrepaymentMonth  = 6;
    this.newPrepaymentAmount = 25_000;
    this.prepayTouched       = {};
    this.prepayErrors        = {};

    if (this.calculated) this.calculate();
  }

  removePrepayment(id: number): void {
    this.prepayments = this.prepayments.filter(p => p.id !== id);
    if (this.calculated) this.calculate();
  }

  calculate(): void {
    this.showFormErrors = true;
    this.touched = {
      loanAmount: true, annualInterestRate: true,
      loanTermYears: true, loanStartDate: true
    };
    const validation = this.validateForm();
    if (!validation.valid) return;

    this.calculating = true;
    this.calcError = false;
    this.cdr.detectChanges();

    try {
      const baseEMI = this.calc.computeEMI(
        this.loanAmount, this.annualInterestRate, this.loanTermYears
      );

      const baseResult: ScheduleResult = this.calc.buildSchedule(
        this.loanAmount, this.annualInterestRate, this.loanTermYears,
        baseEMI, [], this.prepaymentOption, this.loanStartDate
      );
      this.baseSummary = this.calc.buildSummary(baseEMI, baseResult);

      this.hasPrepayments = this.prepayments.length > 0;
      const finalResult: ScheduleResult = this.calc.buildSchedule(
        this.loanAmount, this.annualInterestRate, this.loanTermYears,
        baseEMI, this.prepayments, this.prepaymentOption, this.loanStartDate
      );

      const finalEMI = (this.hasPrepayments && this.prepaymentOption === 'reduce_emi')
        ? finalResult.finalEMI : baseEMI;
      this.finalSummary    = this.calc.buildSummary(finalEMI, finalResult);
      this.monthlySchedule = finalResult.months;
      this.yearlySchedule  = this.calc.buildYearly(finalResult.months);

      this.calculated  = true;
      this.calculating = false;
      this.cdr.detectChanges();

      setTimeout(() => {
        const baseYearly = this.calc.buildYearly(baseResult.months);
        this.charts.render(
          this.pieChartRef.nativeElement,
          this.barChartRef.nativeElement,
          this.lineChartRef.nativeElement,
          {
            loanAmount:     this.loanAmount,
            finalSummary:   this.finalSummary!,
            yearlySchedule: this.yearlySchedule,
            hasPrepayments: this.hasPrepayments,
            monthlySchedule:this.monthlySchedule,
            baseMonths:     this.baseSummary!.totalMonths,
            baseYearly
          }
        );
      }, 80);
    } catch (err) {
      if (!environment.production) {
        console.error('Calculation error', err);
      }
      this.calculating = false;
      this.calcError = true;
    }
  }

  getMonthName(calendarMonth: number): string {
    return this.fmt.monthName(calendarMonth);
  }

  toggleYear(year: number): void {
    this.expandedYear = this.expandedYear === year ? null : year;
  }

  getMonthsForYear(year: number): MonthlyPayment[] {
    return this.monthlySchedule.filter(m => m.calendarYear === year);
  }

  reset(): void {
    this.loanAmount         = 500_000;
    this.annualInterestRate = 13;
    this.loanTermYears      = 3;
    this.loanStartDate      = this.currentMonthStr();
    this.prepayments        = [];
    this.nextId             = 1;
    this.newPrepaymentMonth  = 6;
    this.newPrepaymentAmount = 25_000;
    this.prepaymentOption   = 'reduce_tenure';
    this.formErrors         = {};
    this.prepayErrors       = {};
    this.touched            = {};
    this.prepayTouched      = {};
    this.showFormErrors     = false;
    this.calculated         = false;
    this.calculating        = false;
    this.calcError          = false;
    this.baseSummary        = null;
    this.finalSummary       = null;
    this.monthlySchedule    = [];
    this.yearlySchedule     = [];
    this.expandedYear       = null;
    this.activeTab          = 'yearly';
    this.charts.destroyAll();
  }

  ngOnDestroy(): void {
    this.charts.destroyAll();
  }
}
